import React, { useEffect, useState, createElement } from 'react';
import { useLeaderboard } from '../hooks/useLeaderboard';
import { useContactSubmissions } from '../hooks/useContactSubmissions';
import { useWebhooks, WebhookConfig } from '../hooks/useWebhooks';
import { clearAdminSession } from '../utils/auth';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { LeaderboardEntry } from '../types';
import { LogOutIcon, Trash2Icon, DumbbellIcon, BrainCircuitIcon, PhoneIcon, SearchIcon, ShieldIcon, TrophyIcon, WebhookIcon, DownloadIcon, PlusIcon, EditIcon, CheckIcon, XIcon, SendIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
interface AdminDashboardProps {
  onLogout: () => void;
}
type AdminTab = 'overview' | 'brawn' | 'brains' | 'contacts' | 'webhooks';
function downloadCSV(filename: string, rows: string[][], headers: string[]) {
  const csvContent = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
export function AdminDashboard({
  onLogout
}: AdminDashboardProps) {
  const {
    entries,
    deleteEntry,
    getTopScores
  } = useLeaderboard();
  const {
    submissions,
    deleteSubmission
  } = useContactSubmissions();
  const {
    webhooks,
    loading: webhooksLoading,
    addWebhook,
    updateWebhook,
    deleteWebhook,
    reload: reloadWebhooks
  } = useWebhooks();
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  // Webhook form state
  const [newWebhookName, setNewWebhookName] = useState('');
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [showUrls, setShowUrls] = useState<Record<string, boolean>>({});
  const [pushStatus, setPushStatus] = useState<Record<string, string>>({});
  useEffect(() => {
    if (activeTab === 'webhooks') reloadWebhooks();
  }, [activeTab]);
  const handleLogout = () => {
    clearAdminSession();
    onLogout();
  };
  const handleDelete = (id: string) => {
    if (confirmDelete === id) {
      deleteEntry(id);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(id);
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };
  const handleDeleteContact = (id: string) => {
    if (confirmDelete === id) {
      deleteSubmission(id);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(id);
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };
  const handleDeleteWebhook = (id: string) => {
    if (confirmDelete === id) {
      deleteWebhook(id);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(id);
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };
  const handleAddWebhook = async () => {
    if (!newWebhookName.trim() || !newWebhookUrl.trim()) return;
    await addWebhook(newWebhookName.trim(), newWebhookUrl.trim());
    setNewWebhookName('');
    setNewWebhookUrl('');
  };
  const handleSaveEdit = async () => {
    if (!editingId) return;
    await updateWebhook(editingId, {
      name: editName,
      url: editUrl
    });
    setEditingId(null);
  };
  const handlePushToWebhook = async (hook: WebhookConfig) => {
    setPushStatus(s => ({
      ...s,
      [hook.id]: 'sending'
    }));
    try {
      const res = await fetch(hook.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'admin_test_push',
          timestamp: new Date().toISOString(),
          entries: entries.length,
          contacts: submissions.length
        })
      });
      setPushStatus(s => ({
        ...s,
        [hook.id]: res.ok ? 'success' : 'error'
      }));
    } catch {
      setPushStatus(s => ({
        ...s,
        [hook.id]: 'error'
      }));
    }
    setTimeout(() => setPushStatus(s => {
      const n = {
        ...s
      };
      delete n[hook.id];
      return n;
    }), 3000);
  };
  const brawnEntries = entries.filter(e => e.type === 'brawn').filter(e => !search || e.name.toLowerCase().includes(search.toLowerCase())).sort((a, b) => b.score - a.score);
  const brainsEntries = entries.filter(e => e.type === 'brains').filter(e => !search || e.name.toLowerCase().includes(search.toLowerCase())).sort((a, b) => a.score - b.score);
  const filteredContacts = submissions.filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.email.toLowerCase().includes(search.toLowerCase()));
  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return iso;
    }
  };
  const exportBrawnCSV = () => downloadCSV('brute-scores.csv', brawnEntries.map(e => [e.name, e.firstName || '', e.lastName || '', formatDate(e.date), `${e.score} lbs`]), ['Full Name', 'First Name', 'Last Name', 'Date', 'Score']);
  const exportBrainsCSV = () => downloadCSV('nerd-scores.csv', brainsEntries.map(e => [e.name, e.firstName || '', e.lastName || '', formatDate(e.date), `${e.score}s`, String(e.details?.swipe ?? ''), String(e.details?.swipeTotal ?? '')]), ['Full Name', 'First Name', 'Last Name', 'Date', 'Time (s)', 'Correct', 'Total']);
  const exportContactsCSV = () => downloadCSV('contacts.csv', filteredContacts.map(s => [s.name, s.email, s.phone, formatDate(s.date)]), ['Name', 'Email', 'Phone', 'Date']);
  const tabs: {
    id: AdminTab;
    label: string;
    icon: React.ReactNode;
    count?: number;
  }[] = [{
    id: 'overview',
    label: 'Overview',
    icon: <TrophyIcon className="w-4 h-4" data-id="element-239" />
  }, {
    id: 'brawn',
    label: 'Brute Scores',
    icon: <DumbbellIcon className="w-4 h-4" data-id="element-240" />,
    count: entries.filter(e => e.type === 'brawn').length
  }, {
    id: 'brains',
    label: 'Nerd Scores',
    icon: <BrainCircuitIcon className="w-4 h-4" data-id="element-241" />,
    count: entries.filter(e => e.type === 'brains').length
  }, {
    id: 'contacts',
    label: 'Contacts',
    icon: <PhoneIcon className="w-4 h-4" data-id="element-242" />,
    count: submissions.length
  }, {
    id: 'webhooks',
    label: 'Webhooks',
    icon: <WebhookIcon className="w-4 h-4" data-id="element-243" />,
    count: webhooks.length
  }];
  return <div className="min-h-screen bg-zinc-950 text-white" data-id="element-244">
      <header className="border-b border-zinc-800 bg-zinc-900/80 backdrop-blur sticky top-0 z-20" data-id="element-245">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-246">
          <div className="flex items-center gap-3" data-id="element-247">
            <ShieldIcon className="w-6 h-6 text-orange-500" data-id="element-248" />
            <div data-id="element-249">
              <h1 className="font-black text-white text-lg leading-none" data-id="element-250">
                ADMIN PANEL
              </h1>
              <p className="text-gray-500 text-xs" data-id="element-251">Brutes vs Nerds</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm px-3 py-2 border border-zinc-700 rounded hover:border-zinc-500" data-id="element-252">
            <LogOutIcon className="w-4 h-4" data-id="element-253" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6" data-id="element-254">
        <div className="flex gap-1 mb-6 bg-zinc-900 p-1 rounded-lg border border-zinc-800 overflow-x-auto" data-id="element-255">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded text-sm font-bold whitespace-nowrap transition-all ${activeTab === tab.id ? 'bg-zinc-700 text-white' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-256">
              {tab.icon}
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-zinc-600 text-white' : 'bg-zinc-800 text-gray-400'}`} data-id="element-257">
                  {tab.count}
                </span>}
            </button>)}
        </div>

        {activeTab !== 'overview' && activeTab !== 'webhooks' && <div className="relative mb-4" data-id="element-258">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" data-id="element-259" />
            <input type="text" placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 rounded pl-10 pr-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-zinc-500 text-sm" data-id="element-260" />
          </div>}

        {/* OVERVIEW */}
        {activeTab === 'overview' && <div className="space-y-6" data-id="element-261">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-262">
              <Card className="text-center" data-id="element-263">
                <div className="text-3xl font-black text-white" data-id="element-264">
                  {entries.filter(e => e.type === 'brawn').length}
                </div>
                <div className="text-orange-500 text-sm font-bold mt-1" data-id="element-265">
                  Brute Entries
                </div>
              </Card>
              <Card className="text-center" data-id="element-266">
                <div className="text-3xl font-black text-white" data-id="element-267">
                  {entries.filter(e => e.type === 'brains').length}
                </div>
                <div className="text-cyan-500 text-sm font-bold mt-1 font-mono" data-id="element-268">
                  Nerd Entries
                </div>
              </Card>
              <Card className="text-center" data-id="element-269">
                <div className="text-3xl font-black text-white" data-id="element-270">
                  {submissions.length}
                </div>
                <div className="text-yellow-500 text-sm font-bold mt-1" data-id="element-271">
                  Contacts
                </div>
              </Card>
              <Card className="text-center" data-id="element-272">
                <div className="text-3xl font-black text-white" data-id="element-273">
                  {new Set(entries.map(e => e.name.toLowerCase())).size}
                </div>
                <div className="text-gray-400 text-sm font-bold mt-1" data-id="element-274">
                  Unique Players
                </div>
              </Card>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-275">
              <Card variant="brawn" data-id="element-276">
                <h3 className="text-orange-500 font-black uppercase italic text-lg mb-4" data-id="element-277">
                  Top Brute Scores
                </h3>
                {getTopScores('brawn', 5).length === 0 ? <p className="text-gray-600 text-sm" data-id="element-278">No scores yet.</p> : <div className="space-y-2" data-id="element-279">
                    {getTopScores('brawn', 5).map((e, i) => <div key={e.id} className="flex justify-between items-center py-1 border-b border-orange-900/20" data-id="element-280">
                        <span className="text-gray-400 text-sm w-6" data-id="element-281">
                          #{i + 1}
                        </span>
                        <span className="text-white flex-1 ml-2" data-id="element-282">{e.name}</span>
                        <span className="text-orange-400 font-bold" data-id="element-283">
                          {e.score} lbs
                        </span>
                      </div>)}
                  </div>}
              </Card>
              <Card variant="brains" data-id="element-284">
                <h3 className="text-cyan-500 font-mono font-bold text-lg mb-4" data-id="element-285">
                  Top Nerd Times
                </h3>
                {entries.filter(e => e.type === 'brains').length === 0 ? <p className="text-gray-600 text-sm" data-id="element-286">No scores yet.</p> : <div className="space-y-2" data-id="element-287">
                    {entries.filter(e => e.type === 'brains').sort((a, b) => a.score - b.score).slice(0, 5).map((e, i) => <div key={e.id} className="flex justify-between items-center py-1 border-b border-cyan-900/20" data-id="element-288">
                          <span className="text-gray-400 text-sm w-6" data-id="element-289">
                            #{i + 1}
                          </span>
                          <span className="text-white flex-1 ml-2" data-id="element-290">
                            {e.name}
                          </span>
                          <span className="text-cyan-400 font-mono font-bold" data-id="element-291">
                            {e.score}s
                          </span>
                        </div>)}
                  </div>}
              </Card>
            </div>
          </div>}

        {/* BRAWN */}
        {activeTab === 'brawn' && <div data-id="element-292">
            <div className="flex justify-end mb-3" data-id="element-293">
              <button onClick={exportBrawnCSV} className="flex items-center gap-2 text-sm text-gray-400 hover:text-white border border-zinc-700 hover:border-zinc-500 px-3 py-2 rounded transition-colors" data-id="element-294">
                <DownloadIcon className="w-4 h-4" data-id="element-295" /> Export CSV
              </button>
            </div>
            <div className="overflow-hidden rounded-lg border border-zinc-800" data-id="element-296">
              <table className="w-full text-sm" data-id="element-297">
                <thead className="bg-orange-950/30 text-orange-500 border-b border-zinc-800" data-id="element-298">
                  <tr data-id="element-299">
                    <th className="p-3 text-left font-bold uppercase tracking-wider" data-id="element-300">
                      Name
                    </th>
                    <th className="p-3 text-left font-bold uppercase tracking-wider hidden md:table-cell" data-id="element-301">
                      Date
                    </th>
                    <th className="p-3 text-right font-bold uppercase tracking-wider" data-id="element-302">
                      Score
                    </th>
                    <th className="p-3 text-right font-bold uppercase tracking-wider" data-id="element-303">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/50" data-id="element-304">
                  {brawnEntries.length === 0 ? <tr data-id="element-305">
                      <td colSpan={4} className="p-8 text-center text-gray-600" data-id="element-306">
                        No entries found.
                      </td>
                    </tr> : brawnEntries.map(entry => <tr key={entry.id} className="bg-zinc-900/30 hover:bg-zinc-900/60 transition-colors" data-id="element-307">
                        <td className="p-3 text-white font-medium" data-id="element-308">
                          {entry.name}
                        </td>
                        <td className="p-3 text-gray-500 hidden md:table-cell" data-id="element-309">
                          {formatDate(entry.date)}
                        </td>
                        <td className="p-3 text-right text-orange-400 font-bold" data-id="element-310">
                          {entry.score} lbs
                        </td>
                        <td className="p-3 text-right" data-id="element-311">
                          <button onClick={() => handleDelete(entry.id)} className={`flex items-center gap-1 ml-auto px-3 py-1.5 rounded text-xs font-bold transition-all ${confirmDelete === entry.id ? 'bg-red-600 text-white animate-pulse' : 'bg-zinc-800 text-gray-400 hover:bg-red-950 hover:text-red-400'}`} data-id="element-312">
                            <Trash2Icon className="w-3 h-3" data-id="element-313" />
                            {confirmDelete === entry.id ? 'CONFIRM' : 'Delete'}
                          </button>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </div>}

        {/* BRAINS */}
        {activeTab === 'brains' && <div data-id="element-314">
            <div className="flex justify-end mb-3" data-id="element-315">
              <button onClick={exportBrainsCSV} className="flex items-center gap-2 text-sm text-gray-400 hover:text-white border border-zinc-700 hover:border-zinc-500 px-3 py-2 rounded transition-colors" data-id="element-316">
                <DownloadIcon className="w-4 h-4" data-id="element-317" /> Export CSV
              </button>
            </div>
            <div className="overflow-hidden rounded-lg border border-zinc-800" data-id="element-318">
              <table className="w-full text-sm" data-id="element-319">
                <thead className="bg-cyan-950/30 text-cyan-500 border-b border-zinc-800" data-id="element-320">
                  <tr data-id="element-321">
                    <th className="p-3 text-left font-bold uppercase tracking-wider" data-id="element-322">
                      Name
                    </th>
                    <th className="p-3 text-left font-bold uppercase tracking-wider hidden md:table-cell" data-id="element-323">
                      Date
                    </th>
                    <th className="p-3 text-right font-bold uppercase tracking-wider hidden sm:table-cell" data-id="element-324">
                      Correct
                    </th>
                    <th className="p-3 text-right font-bold uppercase tracking-wider" data-id="element-325">
                      Time
                    </th>
                    <th className="p-3 text-right font-bold uppercase tracking-wider" data-id="element-326">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/50" data-id="element-327">
                  {brainsEntries.length === 0 ? <tr data-id="element-328">
                      <td colSpan={5} className="p-8 text-center text-gray-600" data-id="element-329">
                        No entries found.
                      </td>
                    </tr> : brainsEntries.map(entry => <tr key={entry.id} className="bg-zinc-900/30 hover:bg-zinc-900/60 transition-colors" data-id="element-330">
                        <td className="p-3 text-white font-medium" data-id="element-331">
                          {entry.name}
                        </td>
                        <td className="p-3 text-gray-500 hidden md:table-cell" data-id="element-332">
                          {formatDate(entry.date)}
                        </td>
                        <td className="p-3 text-right text-gray-400 font-mono hidden sm:table-cell" data-id="element-333">
                          {entry.details?.swipe ?? '—'}/
                          {entry.details?.swipeTotal ?? '—'}
                        </td>
                        <td className="p-3 text-right text-cyan-400 font-mono font-bold" data-id="element-334">
                          {entry.score}s
                        </td>
                        <td className="p-3 text-right" data-id="element-335">
                          <button onClick={() => handleDelete(entry.id)} className={`flex items-center gap-1 ml-auto px-3 py-1.5 rounded text-xs font-bold transition-all ${confirmDelete === entry.id ? 'bg-red-600 text-white animate-pulse' : 'bg-zinc-800 text-gray-400 hover:bg-red-950 hover:text-red-400'}`} data-id="element-336">
                            <Trash2Icon className="w-3 h-3" data-id="element-337" />
                            {confirmDelete === entry.id ? 'CONFIRM' : 'Delete'}
                          </button>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </div>}

        {/* CONTACTS */}
        {activeTab === 'contacts' && <div data-id="element-338">
            <div className="flex justify-end mb-3" data-id="element-339">
              <button onClick={exportContactsCSV} className="flex items-center gap-2 text-sm text-gray-400 hover:text-white border border-zinc-700 hover:border-zinc-500 px-3 py-2 rounded transition-colors" data-id="element-340">
                <DownloadIcon className="w-4 h-4" data-id="element-341" /> Export CSV
              </button>
            </div>
            <div className="overflow-hidden rounded-lg border border-zinc-800" data-id="element-342">
              <table className="w-full text-sm" data-id="element-343">
                <thead className="bg-zinc-800/50 text-yellow-500 border-b border-zinc-800" data-id="element-344">
                  <tr data-id="element-345">
                    <th className="p-3 text-left font-bold uppercase tracking-wider" data-id="element-346">
                      Name
                    </th>
                    <th className="p-3 text-left font-bold uppercase tracking-wider" data-id="element-347">
                      Email
                    </th>
                    <th className="p-3 text-left font-bold uppercase tracking-wider hidden md:table-cell" data-id="element-348">
                      Phone
                    </th>
                    <th className="p-3 text-left font-bold uppercase tracking-wider hidden lg:table-cell" data-id="element-349">
                      Date
                    </th>
                    <th className="p-3 text-right font-bold uppercase tracking-wider" data-id="element-350">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/50" data-id="element-351">
                  {filteredContacts.length === 0 ? <tr data-id="element-352">
                      <td colSpan={5} className="p-8 text-center text-gray-600" data-id="element-353">
                        No contact submissions yet.
                      </td>
                    </tr> : filteredContacts.map(sub => <tr key={sub.id} className="bg-zinc-900/30 hover:bg-zinc-900/60 transition-colors" data-id="element-354">
                        <td className="p-3 text-white font-medium" data-id="element-355">
                          {sub.name}
                        </td>
                        <td className="p-3 text-gray-300" data-id="element-356">{sub.email}</td>
                        <td className="p-3 text-gray-400 hidden md:table-cell" data-id="element-357">
                          {sub.phone}
                        </td>
                        <td className="p-3 text-gray-500 hidden lg:table-cell" data-id="element-358">
                          {formatDate(sub.date)}
                        </td>
                        <td className="p-3 text-right" data-id="element-359">
                          <button onClick={() => handleDeleteContact(sub.id)} className={`flex items-center gap-1 ml-auto px-3 py-1.5 rounded text-xs font-bold transition-all ${confirmDelete === sub.id ? 'bg-red-600 text-white animate-pulse' : 'bg-zinc-800 text-gray-400 hover:bg-red-950 hover:text-red-400'}`} data-id="element-360">
                            <Trash2Icon className="w-3 h-3" data-id="element-361" />
                            {confirmDelete === sub.id ? 'CONFIRM' : 'Delete'}
                          </button>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </div>}

        {/* WEBHOOKS */}
        {activeTab === 'webhooks' && <div className="space-y-6" data-id="element-362">
            <div className="bg-amber-950/20 border border-amber-800/40 rounded-lg p-4 text-sm text-amber-200/70" data-id="element-363">
              🔒 Webhook URLs are encrypted with AES-256 and stored only in your
              browser. They are never visible in the page source or to other
              users.
            </div>

            {/* Add new webhook */}
            <Card data-id="element-364">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2" data-id="element-365">
                <PlusIcon className="w-4 h-4 text-green-400" data-id="element-366" /> Add Webhook
              </h3>
              <div className="flex flex-col md:flex-row gap-3" data-id="element-367">
                <input type="text" placeholder="Name (e.g. LetLois CRM)" value={newWebhookName} onChange={e => setNewWebhookName(e.target.value)} className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white placeholder-gray-600 focus:outline-none focus:border-zinc-500 text-sm" data-id="element-368" />
                <input type="url" placeholder="https://..." value={newWebhookUrl} onChange={e => setNewWebhookUrl(e.target.value)} className="flex-[2] bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white placeholder-gray-600 focus:outline-none focus:border-zinc-500 text-sm font-mono" data-id="element-369" />
                <button onClick={handleAddWebhook} disabled={!newWebhookName || !newWebhookUrl} className="px-4 py-2 bg-green-700 hover:bg-green-600 text-white rounded text-sm font-bold disabled:opacity-40 transition-colors flex items-center gap-2" data-id="element-370">
                  <PlusIcon className="w-4 h-4" data-id="element-371" /> Add
                </button>
              </div>
            </Card>

            {/* Webhook list */}
            {webhooksLoading ? <div className="text-center py-8 text-gray-500" data-id="element-372">
                Loading webhooks...
              </div> : webhooks.length === 0 ? <div className="text-center py-8 text-gray-600" data-id="element-373">
                No webhooks configured.
              </div> : <div className="space-y-3" data-id="element-374">
                {webhooks.map(hook => <Card key={hook.id} className="!p-4" data-id="element-375">
                    {editingId === hook.id ? <div className="space-y-3" data-id="element-376">
                        <input type="text" value={editName} onChange={e => setEditName(e.target.value)} className="w-full bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-zinc-400" data-id="element-377" />
                        <input type="url" value={editUrl} onChange={e => setEditUrl(e.target.value)} className="w-full bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-zinc-400" data-id="element-378" />
                        <div className="flex gap-2" data-id="element-379">
                          <button onClick={handleSaveEdit} className="flex items-center gap-1 px-3 py-1.5 bg-green-700 hover:bg-green-600 text-white rounded text-xs font-bold transition-colors" data-id="element-380">
                            <CheckIcon className="w-3 h-3" data-id="element-381" /> Save
                          </button>
                          <button onClick={() => setEditingId(null)} className="flex items-center gap-1 px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white rounded text-xs font-bold transition-colors" data-id="element-382">
                            <XIcon className="w-3 h-3" data-id="element-383" /> Cancel
                          </button>
                        </div>
                      </div> : <div className="flex items-start justify-between gap-4" data-id="element-384">
                        <div className="flex-1 min-w-0" data-id="element-385">
                          <div className="flex items-center gap-2 mb-1" data-id="element-386">
                            <span className="text-white font-bold text-sm" data-id="element-387">
                              {hook.name}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${hook.active ? 'bg-green-900/50 text-green-400' : 'bg-zinc-800 text-gray-500'}`} data-id="element-388">
                              {hook.active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-389">
                            <span className="text-gray-500 text-xs font-mono truncate" data-id="element-390">
                              {showUrls[hook.id] ? hook.url : hook.url.replace(/(?<=.{30}).+(?=.{10})/, '•••••••••••••••')}
                            </span>
                            <button onClick={() => setShowUrls(s => ({
                    ...s,
                    [hook.id]: !s[hook.id]
                  }))} className="text-gray-600 hover:text-gray-400 flex-shrink-0" data-id="element-391">
                              {showUrls[hook.id] ? <EyeOffIcon className="w-3 h-3" data-id="element-392" /> : <EyeIcon className="w-3 h-3" data-id="element-393" />}
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-394">
                          <button onClick={() => handlePushToWebhook(hook)} className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold transition-all ${pushStatus[hook.id] === 'sending' ? 'bg-zinc-700 text-gray-400 animate-pulse' : pushStatus[hook.id] === 'success' ? 'bg-green-700 text-white' : pushStatus[hook.id] === 'error' ? 'bg-red-700 text-white' : 'bg-zinc-700 hover:bg-zinc-600 text-white'}`} data-id="element-395">
                            <SendIcon className="w-3 h-3" data-id="element-396" />
                            {pushStatus[hook.id] === 'sending' ? 'Sending...' : pushStatus[hook.id] === 'success' ? 'Sent!' : pushStatus[hook.id] === 'error' ? 'Failed' : 'Test'}
                          </button>
                          <button onClick={() => updateWebhook(hook.id, {
                  active: !hook.active
                })} className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white rounded text-xs font-bold transition-colors" data-id="element-397">
                            {hook.active ? 'Disable' : 'Enable'}
                          </button>
                          <button onClick={() => {
                  setEditingId(hook.id);
                  setEditName(hook.name);
                  setEditUrl(hook.url);
                }} className="flex items-center gap-1 px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white rounded text-xs font-bold transition-colors" data-id="element-398">
                            <EditIcon className="w-3 h-3" data-id="element-399" />
                          </button>
                          <button onClick={() => handleDeleteWebhook(hook.id)} className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold transition-all ${confirmDelete === hook.id ? 'bg-red-600 text-white animate-pulse' : 'bg-zinc-800 text-gray-400 hover:bg-red-950 hover:text-red-400'}`} data-id="element-400">
                            <Trash2Icon className="w-3 h-3" data-id="element-401" />
                            {confirmDelete === hook.id ? 'CONFIRM' : ''}
                          </button>
                        </div>
                      </div>}
                  </Card>)}
              </div>}
          </div>}
      </div>
    </div>;
}